import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useAuth } from '../context/AuthContext';
import Navbar from './Navbar';
import Footer from './Footer';
import TopBar from './TopBar';
import './Register.css';

const Register = () => {
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    phone: '',
    address: '',
    password: '',
    confirmPassword: '',
  });
  const [loading, setLoading] = useState(false);
  const { register } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const redirect = params.get('redirect');
  const safeRedirect = redirect && redirect.startsWith('/') ? redirect : null;

  const handleChange = e => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async e => {
    e.preventDefault();

    if (formData.password !== formData.confirmPassword) {
      toast.error("Passwords don't match");
      return;
    }

    if (formData.password.length < 8) {
      toast.error('Password must be at least 8 characters long');
      return;
    }

    if (!formData.phone?.trim()) {
      toast.error('Phone is required');
      return;
    }

    setLoading(true);

    try {
      const result = await register(formData);

      if (result.success) {
        toast.success(result.message || 'Registration successful! Please login.');
        setFormData({
          fullName: '',
          email: '',
          phone: '',
          address: '',
          password: '',
          confirmPassword: '',
        });
        const loginPath = safeRedirect
          ? `/login?redirect=${encodeURIComponent(safeRedirect)}`
          : '/login';
        setTimeout(() => navigate(loginPath), 1500);
      } else {
        toast.error(result.error || 'Registration failed');
      }
    } catch (err) {
      toast.error('An unexpected error occurred');
      console.error('Registration error:', err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <TopBar />
      <main className="page-auth">
        <div className="container">
          <Navbar />
          <div className="row">
            <div className="col-lg-10 offset-lg-1">
              <section className="auth-wrapper">
                <div className="row">
                  <div className="col-12">
                    <h2 className="auth-section-title">Create Account</h2>
                    <p className="auth-section-subtitle">Fill in the details to register.</p>

                    <form onSubmit={handleSubmit} className="auth-form" noValidate>
                      <div className="form-row">
                        <div className="form-group col-md-12 mb-2">
                          <label htmlFor="fullName">Full Name</label>
                          <input
                            type="text"
                            className="form-control"
                            id="fullName"
                            name="fullName"
                            placeholder="Your full name"
                            value={formData.fullName}
                            onChange={handleChange}
                            required
                            disabled={loading}
                          />
                        </div>
                        <div className="form-group col-md-12 mb-2">
                          <label htmlFor="email">
                            Email <sup>*</sup>
                          </label>
                          <input
                            type="email"
                            className="form-control"
                            id="email"
                            name="email"
                            placeholder="Email"
                            value={formData.email}
                            onChange={handleChange}
                            required
                            disabled={loading}
                          />
                        </div>
                      </div>

                      <div className="form-row">
                        <div className="form-group col-md-12 mb-2">
                          <label htmlFor="phone">Phone</label>
                          <input
                            type="text"
                            className="form-control"
                            id="phone"
                            name="phone"
                            placeholder="Phone"
                            value={formData.phone}
                            onChange={handleChange}
                            required
                            disabled={loading}
                          />
                        </div>
                        <div className="form-group col-md-12 mb-2">
                          <label htmlFor="address">Address</label>
                          <input
                            type="text"
                            className="form-control"
                            id="address"
                            name="address"
                            placeholder="Address"
                            value={formData.address}
                            onChange={handleChange}
                            disabled={loading}
                          />
                        </div>
                      </div>

                      <div className="form-row">
                        <div className="form-group col-md-12 mb-2">
                          <label htmlFor="password">
                            Password <sup>*</sup>
                          </label>
                          <input
                            type="password"
                            className="form-control"
                            id="password"
                            name="password"
                            placeholder="Password"
                            value={formData.password}
                            onChange={handleChange}
                            required
                            minLength={8}
                            disabled={loading}
                          />
                        </div>
                        <div className="form-group col-md-12 mb-2">
                          <label htmlFor="confirmPassword">
                            Confirm Password <sup>*</sup>
                          </label>
                          <input
                            type="password"
                            className="form-control"
                            id="confirmPassword"
                            name="confirmPassword"
                            placeholder="Confirm Password"
                            value={formData.confirmPassword}
                            onChange={handleChange}
                            required
                            disabled={loading}
                          />
                        </div>
                      </div>

                      <button className="btn btn-primary btn-auth-submit mt-2" type="submit" disabled={loading}>
                        {loading ? (
                          <>
                            <span
                              className="spinner-border spinner-border-sm me-2"
                              role="status"
                              aria-hidden="true"
                            ></span>
                            Creating Account...
                          </>
                        ) : (
                          'Create Account'
                        )}
                      </button>
                    </form>

                    <p className="mb-0 mt-3">
                      <Link
                        to={safeRedirect ? `/login?redirect=${encodeURIComponent(safeRedirect)}` : '/login'}
                        className="text-dark font-weight-bold"
                      >
                        Already have an account? Sign in
                      </Link>
                    </p>
                  </div>
                </div>
              </section>
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </>
  );
};

export default Register;
